// hooks/useChatStream.ts
import { useState, useRef } from "react";
import { postStudyNotebooksByNotebookIdChatByChatIdMessage } from "../client";
import { Message } from "../client";
import { StudyDocument } from "../types";

// legacy for manual chat UI
export function useChatStream(
  notebookId: string,
  selectedDocuments: StudyDocument[]
) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = async (content: string) => {
    // Add user message immediately
    const userMessage: Message = {
      sender: "user",
      content,
    };
    setMessages((prev) => [...prev, userMessage]);

    // Start streaming
    setIsStreaming(true);
    abortControllerRef.current = new AbortController();

    const selectedDocumentIds = selectedDocuments
      .filter((doc) => "id" in doc)
      .map((doc) => doc.id);

    try {
      // =============================================================
      // Call chat API w/ streaming response
      // Expected response: text/event-stream with chunks of text
      // =============================================================
      const { stream } =
        await postStudyNotebooksByNotebookIdChatByChatIdMessage({
          path: {
            notebook_id: notebookId,
            chat_id: "1",
          },
          body: {
            query: userMessage,
            history: messages, // will still be the old messages without userMessage,
            notebook_document_ids: selectedDocumentIds,
          },
        });

      // Create empty assistant message to update incrementally
      setMessages((prev) => [
        ...prev,
        {
          sender: "ai",
          content: "",
        },
      ]);

      // =============================================================
      // Stream processing - handles text/event-stream response
      // Adjust parsing logic if your SSE format is different
      // =============================================================
      let assistantContent = "";
      for await (const data of stream) {
        // this is correct since stream is typed incorrectly (data is the string, not data.data)
        assistantContent += (data as unknown as string).slice(1);

        // Update the last message with accumulated content
        setMessages((prev) => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          updated[lastIndex] = {
            ...updated[lastIndex],
            content: assistantContent,
          };
          return updated;
        });
      }
    } catch (error: any) {
      // Don't show error if user manually stopped streaming
      if (error.name === "AbortError") {
        console.log("Streaming aborted by user");
      } else {
        console.error("Streaming error:", error);

        // =============================================================
        // TODO: Add error handling UI (toast notification, error message, etc.)
        // =============================================================

        // Optionally add error message to chat
        setMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            content: "Sorry, I encountered an error. Please try again.",
          },
        ]);
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  return {
    messages,
    sendMessage,
    isStreaming,
    stopStreaming,
    clearMessages,
  };
}
